/**
 * Cart Handlers
 * Handles all add-to-cart interactions
 */

import { addToCart } from './api'
import { showCrossSellDrawer } from './cross-sell-drawer'

/**
 * Initialize all cart event handlers
 */
export function initializeCartHandlers() {
  // Handle add to cart links (product cards)
  initializeAddToCartLinks()

  // Handle add to cart buttons (single product)
  initializeAddToCartButtons()

  // Initialize drawer handlers
  initializeDrawerHandlers()

  // Initialize quantity controls
  initializeQuantityControls()
}

/**
 * Detect whether an add-to-cart interaction came from the cart-page
 * cross-sell carousel; returns attribution for the tracking headers.
 */
function getCrossSellContext(element) {
  const carousel = element.closest('.cross-sell-carousel')
  if (!carousel) return null

  let position = 0
  const slide = element.closest('.swiper-slide')
  if (slide && slide.parentElement) {
    position = Array.prototype.indexOf.call(slide.parentElement.children, slide) + 1
  }

  return {
    source: 'cart_cross_sell_carousel',
    origin: null,
    position
  }
}

/** How long a carousel control shows "Added" before it resets. */
const CAROUSEL_ADDED_CONFIRMATION_MS = 1600

/** Pending resets, so a second add restarts the confirmation rather than stacking. */
const carouselAddedTimers = new WeakMap()

/**
 * After a carousel add on the cart page: refresh the WooCommerce Cart block
 * through its own data store and leave the slide where it is, reporting the
 * outcome on the control itself. Falls back to a reload when the store is
 * unavailable — the carousel is rebuilt server-side then, and in-cart
 * products are not recommended, so the slide goes with it.
 */
function refreshAfterCarouselAdd(link) {
  const cartStore = window.wp?.data?.dispatch?.('wc/store/cart')

  if (!cartStore?.invalidateResolutionForStore) {
    window.location.reload()
    return
  }

  cartStore.invalidateResolutionForStore()
  markCarouselItemAdded(link)
}

/**
 * Confirm a carousel add on the control itself, mirroring markAdded() in the
 * cross-sell drawer: the card stays put and only the control changes, and it
 * changes back. Dropping the href is what disables it meanwhile —
 * pointer-events alone would still leave it reachable by keyboard — so the
 * href is put back with everything else, letting a customer buy a second one.
 */
function markCarouselItemAdded(link) {
  const label = link.closest('.cross-sell-carousel')?.dataset.addedLabel
  const href = link.getAttribute('href')
  const text = link.textContent
  const ariaLabel = link.getAttribute('aria-label')

  link.classList.add('is-added')
  link.removeAttribute('href')
  link.setAttribute('aria-disabled', 'true')

  if (label) {
    // The card's cart control is icon-only (the label is transparent), so this
    // reads to assistive tech while the tick carries it visually.
    link.textContent = label
    link.setAttribute('aria-label', label)
  }

  window.clearTimeout(carouselAddedTimers.get(link))
  carouselAddedTimers.set(
    link,
    window.setTimeout(() => {
      link.classList.remove('is-added')
      link.removeAttribute('aria-disabled')
      link.textContent = text

      if (href) link.setAttribute('href', href)

      if (ariaLabel) {
        link.setAttribute('aria-label', ariaLabel)
      } else {
        link.removeAttribute('aria-label')
      }

      carouselAddedTimers.delete(link)
    }, CAROUSEL_ADDED_CONFIRMATION_MS)
  )
}

/**
 * Initialize add-to-cart links (product archive pages)
 * Uses event delegation to catch clicks on dynamically added links
 */
function initializeAddToCartLinks() {
  // Use event delegation on document to catch clicks on dynamically added links
  document.addEventListener('click', (event) => {
    // Check if clicked element or a parent is an add-to-cart link
    const link = event.target.closest('a[href*="add-to-cart="], a.add-to-cart-link')

    if (link) {
      handleAddToCartLink(event, link)
    }
  })
}

/**
 * Initialize add-to-cart buttons (single product page)
 */
function initializeAddToCartButtons() {
  const addToCartButtons = document.querySelectorAll('button[name="add-to-cart"]')

  addToCartButtons.forEach(button => {
    button.addEventListener('click', handleAddToCartButton)
  })
}

/**
 * Handle add-to-cart link click
 */
async function handleAddToCartLink(event, link) {
  event.preventDefault()

  // If link wasn't passed, try to get it from event
  if (!link) {
    link = event.currentTarget
  }
  link.classList.add('is-loading')
  link.style.pointerEvents = 'none'

  // Extract product ID from URL params or data attributes
  let productId = null
  let quantity = 1

  if (link.href.includes('add-to-cart=')) {
    const url = new URL(link.href)
    productId = url.searchParams.get('add-to-cart')
    quantity = url.searchParams.get('quantity') || 1
  } else {
    productId = link.dataset.product_id || link.dataset.productId
    quantity = link.dataset.quantity || 1
  }

  if (!productId) {
    console.error('No product ID found in add-to-cart link')
    clearLoadingState(link)
    return
  }

  const crossSell = getCrossSellContext(link)

  try {
    await addToCart(productId, quantity, crossSell)
    clearLoadingState(link)

    if (crossSell) {
      // Cart-page carousel add: update the cart block in place instead of
      // opening the drawer over the cart.
      refreshAfterCarouselAdd(link)
    } else {
      await showCrossSellDrawer(productId)
    }
  } catch (error) {
    console.error('Error adding product to cart:', error)
    clearLoadingState(link)
  }
}

/**
 * Handle add-to-cart button click
 */
async function handleAddToCartButton(event) {
  event.preventDefault()

  const button = event.currentTarget
  const form = button.closest('form')
  const productIdInput = form?.querySelector('input[name="product-id"]')
  const quantityInput = form?.querySelector('input[name="quantity"]')

  if (!button || !productIdInput) return

  button.classList.add('is-loading')
  button.disabled = true

  const productId = productIdInput.value
  const quantity = quantityInput ? quantityInput.value : 1

  try {
    await addToCart(productId, quantity)
    button.classList.remove('is-loading')
    button.disabled = false
    await showCrossSellDrawer(productId)
  } catch (error) {
    console.error('Error adding product to cart:', error)
    button.classList.remove('is-loading')
    button.disabled = false
  }
}

/**
 * Initialize drawer event handlers
 */
function initializeDrawerHandlers() {
  // Import drawer handlers dynamically to avoid circular deps
  import('./drawer').then(({ initializeDrawerEvents }) => {
    initializeDrawerEvents()
  })

  // Cross-sell drawer (opens automatically after add-to-cart)
  import('./cross-sell-drawer').then(({ initializeCrossSellDrawerEvents }) => {
    initializeCrossSellDrawerEvents()
  })
}

/**
 * Initialize quantity controls
 */
function initializeQuantityControls() {
  // Generic quantity UI (plus/minus buttons)
  import('../../common/quantity').then(({ initQtyControls }) => {
    initQtyControls('.quantity')
  })

  // WooCommerce cart-specific quantity updates
  import('./quantity').then(({ initializeCartQuantityHandlers }) => {
    initializeCartQuantityHandlers()
  })
}

/**
 * Clear loading state from link
 */
function clearLoadingState(link) {
  link.classList.remove('is-loading')
  link.style.pointerEvents = ''
}
